import { ExchangeRates } from './currencyUtils';
import { fallbackData } from './fallbackData';

export const convertHashrate = (hashrate: number, fromUnit: string, toUnit: string): number => {
  const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s', 'EH/s'];
  const fromIndex = units.indexOf(fromUnit);
  const toIndex = units.indexOf(toUnit);
  
  // Handle invalid units
  if (fromIndex === -1 || toIndex === -1) {
    console.warn('Invalid hashrate unit:', { fromUnit, toUnit });
    return hashrate;
  }
  
  const difference = fromIndex - toIndex;
  return hashrate * Math.pow(1000, difference);
};

const BLOCK_TIMES = {
  BTC: 600, // 10 minutes
  LTC: 150, // 2.5 minutes
  ETH: 12,  // 12 seconds
  KDA: 30,  // 30 seconds
  BCH: 600, // 10 minutes
  DASH: 150, // 2.5 minutes
  ZEC: 75,  // 75 seconds
  DOGE: 60  // 1 minute
};

const BLOCKS_PER_DAY = Object.entries(BLOCK_TIMES).reduce((acc, [coin, time]) => ({
  ...acc,
  [coin]: (24 * 60 * 60) / time
}), {} as Record<string, number>);

export const calculateDailyRewards = (
  minerHashrate: number,
  minerHashrateUnit: string,
  networkHashrate: number,
  networkHashrateUnit: string,
  blockReward: number,
  coinType: string,
  feeReward: number = 1.0
): number => {
  try {
    // Input validation
    if (!minerHashrate || !networkHashrate || !blockReward) {
      return 0;
    }

    if (minerHashrate <= 0 || networkHashrate <= 0 || blockReward <= 0) {
      return 0;
    }

    // Convert hashrates to same unit (TH/s)
    const normalizedMinerHashrate = convertHashrate(minerHashrate, minerHashrateUnit, 'TH/s');
    const normalizedNetworkHashrate = convertHashrate(networkHashrate, networkHashrateUnit, 'TH/s');

    if (normalizedNetworkHashrate === 0) return 0;

    // Calculate miner's share of network hashrate
    const hashrateShare = normalizedMinerHashrate / normalizedNetworkHashrate;
    
    // Get daily blocks for coin type
    const dailyBlocks = BLOCKS_PER_DAY[coinType] || 0;
    if (dailyBlocks === 0) {
      console.warn('Unknown coin type for block calculation:', coinType);
      return 0;
    }

    // Calculate daily rewards
    const dailyBaseRewards = hashrateShare * dailyBlocks * blockReward;
    
    // Apply fee rewards multiplier (default 1.0 if not specified)
    return Math.max(0, dailyBaseRewards * (feeReward || 1.0));
  } catch (error) {
    console.error('Error calculating daily rewards:', error);
    return 0;
  }
};

export const calculateDailyPowerCost = (powerWatts: number, powerCost: number): number => {
  try {
    // Input validation
    if (!powerWatts || !powerCost) {
      return 0;
    }
    
    if (powerWatts <= 0 || powerCost <= 0) {
      return 0;
    }
    
    // Convert watts to kilowatts and multiply by 24 hours
    const dailyKWh = (powerWatts / 1000) * 24;
    
    // Calculate daily cost
    return Math.max(0, dailyKWh * powerCost);
  } catch (error) {
    console.error('Error calculating power cost:', error);
    return 0;
  }
};

export const calculateProfitability = (
  miner: any,
  marketData: any,
  powerCost: number,
  currency: string,
  exchangeRates: ExchangeRates
) => {
  try {
    // Input validation
    if (!miner || !marketData || powerCost === undefined) {
      console.warn('Missing required inputs for profitability calculation');
      return getDefaultProfitability();
    }

    const coinData = marketData[miner.coin] || fallbackData[miner.coin];
    if (!coinData) {
      console.warn('No market data available for coin:', miner.coin);
      return getDefaultProfitability();
    }
    
    const dailyRewards = calculateDailyRewards(
      miner.hashrate,
      miner.hashrateUnit,
      coinData.networkHashrate,
      'TH/s',
      coinData.blockReward,
      miner.coin,
      coinData.feeReward
    );

    const dailyRevenueUSD = dailyRewards * (coinData.price || 0);
    const dailyPowerCost = calculateDailyPowerCost(miner.power, powerCost);
    const dailyProfit = dailyRevenueUSD - dailyPowerCost;

    const monthlyProfit = dailyProfit * 30;
    const yearlyProfit = dailyProfit * 365;

    return {
      dailyRewards,
      dailyRevenueUSD,
      dailyPowerCost,
      dailyProfit,
      monthlyProfit,
      yearlyProfit,
      revenue24: dailyRevenueUSD,
      cost24: dailyPowerCost,
      profit24: dailyProfit,
      profit30: monthlyProfit,
      profit365: yearlyProfit,
      rewardsUSD: coinData.price || 0
    };
  } catch (error) {
    console.error('Error calculating profitability:', error);
    return getDefaultProfitability();
  }
};

function getDefaultProfitability() {
  return {
    dailyRewards: 0,
    dailyRevenueUSD: 0,
    dailyPowerCost: 0,
    dailyProfit: 0,
    monthlyProfit: 0,
    yearlyProfit: 0,
    revenue24: 0,
    cost24: 0,
    profit24: 0,
    profit30: 0,
    profit365: 0,
    rewardsUSD: 0
  };
}